import { useCallback, useEffect, useRef, useState } from 'react';
import type { CapturePhotoRow } from '../../data/database.types';
import { selectImageRaster } from '../../platform/image-raster';
import { selectReportFile, type ReportFileHandle } from '../../platform/report-file';
import { getSupabaseClient } from '../../data/supabase-client';
import { createSupabasePhotoImageUrls } from '../../data/photos/photo-images';
import type { ReportDocument } from './report-document';
import type { SavedReportRow } from '../../data/database.types';
import {
  ReportBrandingUnavailable,
  reportContentFingerprint,
  type ReportPdfArtifact,
} from './pdf/report-pdf-artifact';
import { ReportPhotoUnavailable } from './pdf/report-pdf-images';

/**
 * THE PDF, AS A SCREEN STATE — M10 / S10-04.
 *
 * **ONE ARTIFACT, VIEWED AND SHARED.** Generating produces bytes once; View and
 * Share both act on that same file. Regenerating on either would mean the
 * document Brandon inspected was not the document his client received, which is
 * the whole reason this state exists rather than two independent buttons.
 *
 * **A CHANGED REPORT INVALIDATES THE FILE, AUTOMATICALLY.** If he steps back,
 * adds a photo and returns, the artifact no longer describes his report — so it
 * is discarded and the screen returns to **Generate PDF**. The comparison is a
 * fingerprint of the CONTENT MODEL, so anything a client would notice counts and
 * nothing else does.
 *
 * **THE PDF ENGINE IS LOADED ON DEMAND.** `import()` inside the action keeps
 * jsPDF and the fonts out of the startup bundle: opening Crewlink costs nothing
 * for a feature Brandon may not use today.
 */

export type ReportPdfStatus = 'idle' | 'generating' | 'ready' | 'failed';

/** Durability of the CURRENT artifact. Not a workflow, not a report status. */
export type ReportSaveStatus = 'unsaved' | 'saving' | 'saved' | 'failed';

export interface ReportPdfState {
  readonly status: ReportPdfStatus;
  readonly pageCount: number;
  /** Safe to show. Never a URL, never a stack, never an id. */
  readonly message: string | null;
  readonly canShare: boolean;
  /** Durability of the artifact currently on screen. */
  readonly saveStatus: ReportSaveStatus;
  readonly saveMessage: string | null;
  generate(): Promise<void>;
  view(): Promise<void>;
  share(): Promise<void>;
  save(): Promise<void>;
}

export interface UseReportPdfInput {
  readonly document: ReportDocument;
  /** The server row behind every selected photo, keyed by photo key. */
  readonly rows: ReadonlyMap<string, CapturePhotoRow>;
  readonly timeZone: string | null;
  readonly projectId: string;
}

const GENERIC_FAILURE = 'The PDF could not be created. Nothing was saved.';

export function useReportPdf(input: UseReportPdfInput): ReportPdfState {
  const [files] = useState(() => selectReportFile());
  const [raster] = useState(() => selectImageRaster());
  const [phase, setPhase] = useState<'idle' | 'generating' | 'failed'>('idle');
  /**
   * The durable identity of the artifact on screen, minted WITH it.
   *
   * **A NEW GENERATION MINTS A NEW ID; A RETRY OF THE SAME SAVE REUSES IT.**
   * That is the whole difference between "try again" and "save another report",
   * and it is why a lost response cannot create two saved reports.
   */
  const [saved, setSaved] = useState<{ fingerprint: string; row: SavedReportRow } | null>(null);
  const [savePhase, setSavePhase] = useState<'unsaved' | 'saving' | 'failed'>('unsaved');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const savingRef = useRef(false);
  const [artifact, setArtifact] = useState<
    (ReportPdfArtifact & { readonly reportId: string }) | null
  >(null);
  const [message, setMessage] = useState<string | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [canShare, setCanShare] = useState(false);

  const handleRef = useRef<ReportFileHandle | null>(null);
  const busyRef = useRef(false);

  const fingerprint = reportContentFingerprint(input.document);

  /**
   * **STALENESS IS DERIVED, NOT SYNCHRONISED.** The artifact is only the current
   * report's while its fingerprint matches; the moment Brandon changes a
   * selection it stops counting, with no effect, no cascade and no window in
   * which the screen offers to share the wrong bytes. Its file is cleaned up by
   * the next generation, or when the screen closes.
   */
  const live = artifact && artifact.fingerprint === fingerprint ? artifact : null;
  const status: ReportPdfStatus =
    phase === 'generating'
      ? 'generating'
      : live
        ? 'ready'
        : phase === 'failed' && failedFor === fingerprint
          ? 'failed'
          : 'idle';

  useEffect(() => {
    let alive = true;
    void files.canShare().then((value) => {
      if (alive) setCanShare(value);
    });
    return () => {
      alive = false;
    };
  }, [files]);

  /** The temporary file belongs to this screen; it leaves with it. */
  useEffect(() => {
    return () => {
      const handle = handleRef.current;
      handleRef.current = null;
      if (handle) void files.discard(handle);
    };
  }, [files]);

  const generate = useCallback(async () => {
    // A second tap while the first is still working would produce two
    // documents and leak one of them.
    if (busyRef.current) return;
    busyRef.current = true;
    setPhase('generating');
    setMessage(null);

    try {
      const client = getSupabaseClient();
      if (!client) throw new Error('no-client');

      const { generateReportPdf } = await import('./pdf/generate-report-pdf');
      const { createPhotoFetcher } = await import('./pdf/report-pdf-images');

      const next = await generateReportPdf({
        document: input.document,
        rows: input.rows,
        fetcher: createPhotoFetcher(createSupabasePhotoImageUrls(client)),
        raster,
        timeZone: input.timeZone ?? 'UTC',
        now: new Date(),
      });

      // The previous artifact is replaced, and its file goes with it.
      const previous = handleRef.current;
      handleRef.current = await files.write(next.filename, next.bytes);
      if (previous) void files.discard(previous);
      // Housekeeping, never blocking: yesterday's abandoned artifacts.
      void files.sweepStale(Date.now(), handleRef.current);

      // **A NEW ARTIFACT IS A NEW REPORT.** Minted here so a retry of the save
      // reuses one id while a fresh generation never can.
      setArtifact({ ...next, reportId: crypto.randomUUID() });
      setPhase('idle');
      setSavePhase('unsaved');
      setSaveMessage(null);
    } catch (error) {
      setArtifact(null);
      setPhase('failed');
      setFailedFor(fingerprint);
      setMessage(describeFailure(error));
    } finally {
      busyRef.current = false;
    }
  }, [files, fingerprint, input.document, input.rows, input.timeZone, raster]);

  const view = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle) return;
    try {
      await files.view(handle);
      setMessage(null);
    } catch {
      // The document is untouched and still shareable — say so rather than
      // throwing away work.
      setMessage('This device could not open the PDF. You can still share it.');
    }
  }, [files]);

  const share = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle) return;
    try {
      // **CANCELLING IS NOT FAILING.** The outcome is read so the distinction
      // is explicit in the code, and neither case says anything alarming: the
      // report is generated, and it stays generated.
      const outcome = await files.share(handle, { title: live?.filename ?? 'Report' });
      if (outcome === 'cancelled' || outcome === 'shared') setMessage(null);
    } catch {
      setMessage('Sharing did not start. The PDF is still ready.');
    }
  }, [files, live]);

  /**
   * **RESERVE → UPLOAD → FINALIZE, and nothing says Saved until it is.** The
   * exact generated bytes are uploaded; nothing is regenerated, re-encoded or
   * reconstructed. A failed save leaves the artifact usable so Brandon can try
   * again with the same id.
   */
  const save = useCallback(async () => {
    if (savingRef.current) return;
    const current = live;
    // Stale bytes can never be saved: `live` is null the moment the report's
    // content changes, which is S10-04's invalidation and the ONLY staleness
    // mechanism — a second one here could drift from it.
    if (!current) return;
    if (saved && saved.fingerprint === current.fingerprint) return;

    savingRef.current = true;
    setSavePhase('saving');
    setSaveMessage(null);

    try {
      const client = getSupabaseClient();
      if (!client) throw new Error('no-client');

      const { saveGeneratedReport } = await import('../../data/writers/save-generated-report');
      const { buildSavedReportSnapshot } = await import('./saved/saved-report-snapshot');

      const result = await saveGeneratedReport(client, {
        reportId: current.reportId,
        projectId: input.projectId,
        reportDate: input.document.reportDate,
        pageCount: current.pageCount,
        filename: current.filename,
        bytes: current.bytes,
        snapshot: buildSavedReportSnapshot(input.document),
      });

      if (!result.ok) {
        setSavePhase('failed');
        setSaveMessage(result.message);
        return;
      }

      setSaved({ fingerprint: current.fingerprint, row: result.row });
      setSavePhase('unsaved');
    } catch {
      setSavePhase('failed');
      setSaveMessage('The report could not be saved. Nothing was stored — please try again.');
    } finally {
      savingRef.current = false;
    }
  }, [input.document, input.projectId, live, saved]);

  /**
   * **SAVED IS A PROPERTY OF THESE BYTES, NOT OF THE SCREEN.** Comparing
   * fingerprints means a regenerated report is unsaved again automatically,
   * with no second invalidation rule to drift.
   */
  const saveStatus: ReportSaveStatus =
    savePhase === 'saving'
      ? 'saving'
      : live && saved && saved.fingerprint === live.fingerprint
        ? 'saved'
        : savePhase === 'failed'
          ? 'failed'
          : 'unsaved';

  return {
    status,
    saveStatus,
    saveMessage: saveStatus === 'failed' ? saveMessage : null,
    save,
    pageCount: live?.pageCount ?? 0,
    message,
    canShare,
    generate,
    view,
    share,
  };
}

/** Client-safe wording. Never a URL, an id, or a library message. */
export function describeFailure(error: unknown): string {
  if (error instanceof ReportPhotoUnavailable) {
    const where = error.context.capture
      ? `${error.context.capture} (${error.context.group})`
      : `a ${error.context.group} photo`;
    return `A photo from ${where} could not be included, so nothing was created. Check your connection and try again.`;
  }
  if (error instanceof ReportBrandingUnavailable) {
    return 'Your company logo could not be included, so nothing was created. Try again, or turn company branding off for this report.';
  }
  return GENERIC_FAILURE;
}
